'use client';

import { SaveIcon, UploadIcon } from '@/components';
import { PhoneMaskInput } from '@/components/common';
import { DirectLinkHint } from './components/DirectLinkHint';
import {
  Box,
  Button,
  CircularProgress,
  InputBaseComponentProps,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslations } from 'next-intl';
import { FC, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';

const QuillEditor = dynamic(
  () => import('./components/QuillEditor/QuillEditor').then((m) => m.QuillEditor),
  { ssr: false },
);
import { COLORS, PATHS } from '@/constants';
import { bumpOfficeProfileImageVersion, normalizeUrl } from '@/common/helpers';
import { useFormik } from 'formik';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetOfficeProfile,
  useUpdateOfficeProfile,
  useUploadOfficeBigLogo,
  useUploadOfficeSmallLogo,
} from '@/queris';
import noImage from '@/assets/webp/no-image.webp';
import { toast } from 'react-toastify';
import { showToast } from '@/utils/showToast';
import { OfficeProfileRo } from '@/types';
import { YearPickerField } from './components/YearPickerField';
import { BrokerActiveLocations } from './components/BrokerActiveLocations';
import { BrokerProfileSkeleton } from './components/BrokerProfileSkeleton';
import { BrokerProfilePrices } from './components/BrokerProfilePrices';
import {
  ALLOWED_IMAGE_ACCEPT,
  ALLOWED_IMAGE_MIME_TYPES,
  MAX_BANNER_FILE_SIZE_BYTES,
  MAX_BANNER_FILE_SIZE_MB,
  MAX_SMALL_LOGO_FILE_SIZE_BYTES,
  MAX_SMALL_LOGO_FILE_SIZE_MB,
  formatFileSizeMb,
  formatMaxSizeMbLabel,
  getInitialValues,
  validationSchema,
} from './constants/schema';
import {
  BannerOverlay,
  BannerWrapper,
  CompanyLogo,
  CompanyNameRow,
  Container,
  ContactsGrid,
  ContentCard,
  HeroBanner,
  LogoUploadOverlay,
  LogoWrapper,
  QuillWrapper,
  Section,
  SectionTitle,
} from './styled';

const readImageFile = (
  file: File,
  onLoad: (result: { dataUrl: string; contentType: string }) => void,
) => {
  if (!file.type.startsWith('image/')) return;
  const reader = new FileReader();
  reader.onload = (e) => {
    if (e.target?.result) {
      onLoad({ dataUrl: e.target.result as string, contentType: file.type });
    }
  };
  reader.readAsDataURL(file);
};

export const BrokerProfileEditScreen: FC = () => {
  const t = useTranslations('memberProfile');
  const router = useRouter();

  const queryClient = useQueryClient();
  const { data: profile, isLoading } = useGetOfficeProfile();
  const { mutateAsync: updateProfile } = useUpdateOfficeProfile();
  const { mutateAsync: uploadSmallLogo } = useUploadOfficeSmallLogo();
  const { mutateAsync: uploadBigLogo } = useUploadOfficeBigLogo();

  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [bannerFile, setBannerFile] = useState<{ dataUrl: string; contentType: string } | null>(
    null,
  );
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<{ dataUrl: string; contentType: string } | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);

  useEffect(() => {
    if (profile?.bigLogo && !bannerFile) setBannerPreview(profile.bigLogo);
  }, [profile?.bigLogo, bannerFile]);

  useEffect(() => {
    if (profile?.smallLogo && !logoFile) setLogoPreview(profile.smallLogo);
  }, [profile?.smallLogo, logoFile]);

  const bannerInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const uploadImages = async () => {
    const results: { label: string; ok: boolean }[] = [];

    if (logoFile) {
      try {
        await uploadSmallLogo({ file: logoFile.dataUrl, contentType: logoFile.contentType });
        results.push({ label: 'smallLogo', ok: true });
      } catch {
        results.push({ label: 'smallLogo', ok: false });
      }
    }

    if (bannerFile) {
      try {
        await uploadBigLogo({ file: bannerFile.dataUrl, contentType: bannerFile.contentType });
        results.push({ label: 'bigLogo', ok: true });
      } catch {
        results.push({ label: 'bigLogo', ok: false });
      }
    }

    const smallLogoResult = results.find((r) => r.label === 'smallLogo');
    const bigLogoResult = results.find((r) => r.label === 'bigLogo');

    if (smallLogoResult && !smallLogoResult.ok) toast.error(t('toast.smallLogoError'));
    if (bigLogoResult && !bigLogoResult.ok) toast.error(t('toast.bigLogoError'));

    const hadImageUpload = Boolean(smallLogoResult?.ok || bigLogoResult?.ok);

    if (hadImageUpload) {
      bumpOfficeProfileImageVersion();

      queryClient.setQueryData<OfficeProfileRo>(['office-profile'], (old) => ({
        ...(old ?? {}),
        ...(smallLogoResult?.ok && logoFile ? { smallLogo: logoFile.dataUrl } : {}),
        ...(bigLogoResult?.ok && bannerFile ? { bigLogo: bannerFile.dataUrl } : {}),
      }));

      if (smallLogoResult?.ok) setLogoFile(null);
      if (bigLogoResult?.ok) setBannerFile(null);
    }

    return results;
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getInitialValues(profile),
    validationSchema: validationSchema(t),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values) => {
      let profileOk = false;

      try {
        await updateProfile({
          directLink: values.directLink,
          email: values.email,
          phone: values.phone,
          site: values.website?.trim() ? normalizeUrl(values.website.trim()) : values.website,
          address: values.address,
          region: values.regions,
          founded: values.founded ? Number(values.founded) : undefined,
          aboutUs: values.about,
        });
        profileOk = true;
        showToast.success(t('toast.profileSuccess'));
      } catch {
        toast.error(t('toast.profileError'));
      }

      void queryClient.invalidateQueries({ queryKey: ['office-profile'] });

      if (profileOk) {
        router.push(`/profile/${PATHS.MEMBER_PROFILE}`);
      }
    },
  });

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      await uploadImages();
      await formik.submitForm();
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (formik.submitCount > 0) {
      void formik.validateForm();
    }
  }, [formik.values, formik.submitCount]);

  const showFieldError = (field: keyof typeof formik.values) =>
    !!formik.errors[field] && (formik.touched[field] || formik.submitCount > 0);

  const fieldHelperText = (field: keyof typeof formik.values) =>
    formik.touched[field] || formik.submitCount > 0 ? formik.errors[field] : undefined;

  const isAllowedMimeType = (type: string): boolean =>
    (ALLOWED_IMAGE_MIME_TYPES as readonly string[]).includes(type);

  const handleBannerFileSelect = (file: File) => {
    if (!isAllowedMimeType(file.type)) {
      setBannerError(t('toast.imageFormatError'));
      if (bannerInputRef.current) bannerInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_BANNER_FILE_SIZE_BYTES) {
      setBannerError(
        t('toast.imageSizeError', {
          maxSize: formatMaxSizeMbLabel(MAX_BANNER_FILE_SIZE_MB),
          currentSize: formatFileSizeMb(file.size),
        }),
      );
      if (bannerInputRef.current) bannerInputRef.current.value = '';
      return;
    }
    setBannerError(null);
    readImageFile(file, (result) => {
      setBannerPreview(result.dataUrl);
      setBannerFile(result);
    });
  };

  const handleLogoFileSelect = (file: File) => {
    if (!isAllowedMimeType(file.type)) {
      setLogoError(t('toast.imageFormatError'));
      if (logoInputRef.current) logoInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_SMALL_LOGO_FILE_SIZE_BYTES) {
      setLogoError(
        t('toast.imageSizeError', {
          maxSize: formatMaxSizeMbLabel(MAX_SMALL_LOGO_FILE_SIZE_MB),
          currentSize: formatFileSizeMb(file.size),
        }),
      );
      if (logoInputRef.current) logoInputRef.current.value = '';
      return;
    }
    setLogoError(null);
    readImageFile(file, (result) => {
      setLogoPreview(result.dataUrl);
      setLogoFile(result);
    });
  };

  if (isLoading) {
    return <BrokerProfileSkeleton />;
  }

  return (
    <Container>
      <Typography
        fontSize={30}
        fontWeight={700}
        color={COLORS['primary/900']}
        lineHeight={1}
        marginBottom="22px"
      >
        {t('editTitle')}
      </Typography>

      <form onSubmit={(e) => e.preventDefault()}>
        <ContentCard>
          <BannerWrapper
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files[0];
              if (file) handleBannerFileSelect(file);
            }}
          >
            <HeroBanner>
              <img
                src={bannerPreview ?? undefined}
                alt="hero"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = noImage.src;
                }}
              />
            </HeroBanner>
            <BannerOverlay onClick={() => bannerInputRef.current?.click()}>
              <UploadIcon />
              <Typography fontSize={14} fontWeight={500} color="white">
                {t('bannerChange')}
              </Typography>
              <Typography fontSize={11} fontWeight={400} color="rgba(255,255,255,0.7)">
                {t('bannerHint')}
              </Typography>
            </BannerOverlay>
            <input
              ref={bannerInputRef}
              type="file"
              accept={ALLOWED_IMAGE_ACCEPT}
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleBannerFileSelect(file);
                }
              }}
            />
          </BannerWrapper>
          {bannerError && (
            <Typography fontSize={12} color="error" sx={{ px: '24px', py: '10px' }}>
              {bannerError}
            </Typography>
          )}

          <Section>
            <CompanyNameRow>
              <LogoWrapper
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files[0];
                  if (file) {
                    handleLogoFileSelect(file);
                  }
                }}
              >
                <CompanyLogo>
                  <img
                    src={logoPreview ?? undefined}
                    alt="logo"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = noImage.src;
                    }}
                  />
                </CompanyLogo>
                <LogoUploadOverlay onClick={() => logoInputRef.current?.click()}>
                  <UploadIcon width={28} height={28} />
                  <Typography
                    fontSize={9}
                    fontWeight={400}
                    color="rgba(255,255,255,0.7)"
                    lineHeight={1}
                  >
                    {t('logoHint')}
                  </Typography>
                </LogoUploadOverlay>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept={ALLOWED_IMAGE_ACCEPT}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleLogoFileSelect(file);
                    }
                  }}
                />
              </LogoWrapper>
              <Typography
                fontSize={22}
                fontWeight={700}
                color={COLORS['primary/900']}
                width={'100%'}
              >
                {profile?.name || '—'}
              </Typography>
              <Button
                variant="primary"
                type="button"
                onClick={handleSave}
                disabled={isSubmitting}
                sx={{ height: '40px', width: '140px', flexShrink: 0, gap: '6px' }}
              >
                {isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveIcon width={16} height={16} />
                )}
                {t('saveButton')}
              </Button>
            </CompanyNameRow>
            {logoError && (
              <Typography fontSize={12} color="error">
                {logoError}
              </Typography>
            )}
          </Section>

          <Section>
            <SectionTitle>{t('aboutUs.title')}</SectionTitle>
            <QuillWrapper>
              <QuillEditor
                value={formik.values.about}
                onChange={(value) => formik.setFieldValue('about', value)}
                placeholder={t('aboutUs.placeholder')}
              />
            </QuillWrapper>
            <Box display="flex" alignItems="flex-start" gap="8px">
              <TextField
                {...formik.getFieldProps('directLink')}
                fullWidth
                size="small"
                label={t('form.directLink')}
                error={showFieldError('directLink')}
                helperText={fieldHelperText('directLink')}
              />
              <Box sx={{ mt: '9px' }}>
                <DirectLinkHint
                  value={formik.values.directLink}
                  descriptionLabel={t('form.directLinkHint')}
                  allowedCharsLabel={t('form.directLinkHintAllowed')}
                  yourEntryLabel={t('form.directLinkHintYourEntry')}
                  yourLinkLabel={t('form.directLinkHintYourLink')}
                />
              </Box>
            </Box>
            {/* TODO: PROP-902 раскомментировать когда directLink будет готов на бэкенде
            <TextField
              fullWidth
              size="small"
              label={t('form.urlPreview')}
              value={urlPreview}
              InputProps={{ readOnly: true }}
            /> */}
          </Section>

          <Section>
            <SectionTitle>{t('contacts.title')}</SectionTitle>
            <ContactsGrid>
              <TextField
                {...formik.getFieldProps('email')}
                fullWidth
                size="small"
                label={t('contacts.email')}
                error={showFieldError('email')}
                helperText={fieldHelperText('email')}
              />
              <TextField
                {...formik.getFieldProps('phone')}
                fullWidth
                size="small"
                type="tel"
                label={t('contacts.phone')}
                error={showFieldError('phone')}
                helperText={fieldHelperText('phone')}
                InputProps={{
                  inputComponent: PhoneMaskInput as React.ElementType<InputBaseComponentProps>,
                }}
              />
              <TextField
                {...formik.getFieldProps('website')}
                fullWidth
                size="small"
                label={t('contacts.website')}
                error={showFieldError('website')}
                helperText={fieldHelperText('website')}
              />
              <TextField
                {...formik.getFieldProps('address')}
                fullWidth
                size="small"
                label={t('contacts.address')}
              />
              <YearPickerField
                value={formik.values.founded ?? ''}
                onChange={(year) => formik.setFieldValue('founded', year)}
                label={t('contacts.founded')}
              />
              <TextField
                {...formik.getFieldProps('regions')}
                fullWidth
                size="small"
                label={t('contacts.regions')}
              />
            </ContactsGrid>
          </Section>

          <BrokerActiveLocations activeLocations={profile?.activeLocations} />

          <BrokerProfilePrices directLink={profile?.directLink} />
        </ContentCard>
      </form>
    </Container>
  );
};
